import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  OnInit,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HxBadge } from '../badge/badge';
import { HxButton } from '../button/button';
import { HxIconButton } from '../icon-button/icon-button';
import { HxInput } from '../input/input';
import { HxDropdownComponent } from '../select/dropdown/dropdown.component';
import { PageNumberButton } from './page-number-button';
import { PaginationEvent } from './pagination-event';
import { RowsPerPageOption } from './rows-per-page-option';

@Component({
  selector: 'hx-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule, HxBadge, HxButton, HxDropdownComponent, HxIconButton, HxInput],
  templateUrl: './pagination.html',
  styleUrls: ['./pagination.css'],
  host: {
    class: 'hx-pagination',
    role: 'group',
    '[attr.aria-label]': '"Pagination controls"',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxPagination implements OnInit {
  // ============================================================
  // INPUTS - Required
  // ============================================================

  /** Total number of items across all pages Required input that drives pagination calculations */
  readonly totalRecordCount = input.required<number>();

  /** Show the total number of records alongside the pagination controls */
  readonly showTotalRecordCount = input<boolean>(true);

  /** Label shown before the total record count */
  readonly totalRecordCountLabel = input<string>('Results:');

  // ============================================================
  // INPUTS - Optional Configuration
  // ============================================================

  /**
   * Available page size options for dropdown
   *
   * @default
   */
  readonly pageSizeOptions = input<RowsPerPageOption[]>([
    { id: 'page-size-10', value: 10, label: '10' },
    { id: 'page-size-25', value: 25, label: '25' },
    { id: 'page-size-50', value: 50, label: '50' },
    { id: 'page-size-100', value: 100, label: '100' },
  ]);

  /**
   * Initial page size Used only in uncontrolled mode; ignored if [(pageSize)] is bound If not in pageSizeOptions, will
   * default to first option
   *
   * @default 10
   */
  readonly initialPageSize = input<number>(10);

  /**
   * Label text for page size selector
   *
   * @default 'Rows per page:'
   */
  readonly rowsPerPageLabel = input<string>('Rows per page:');

  /**
   * Label text for go-to-page input
   *
   * @default 'Go to page'
   */
  readonly gotoPageLabel = input<string>('Go to page');

  /**
   * Disable all pagination controls
   *
   * @default false
   */
  readonly disabled = input<boolean>(false);

  /**
   * Show or hide the go-to-page input section
   *
   * @default true
   */
  readonly showGotoPage = input<boolean>(true);

  /**
   * Error message to display when go-to-page input is invalid
   *
   * @default 'Page number is invalid'
   */
  readonly gotoPageErrorMessage = input<string>('Page number is invalid');

  // ============================================================
  // TWO-WAY BINDINGS - Optional for Controlled Mode
  // ============================================================

  /**
   * Current page number (1-indexed) Can be bound with [(currentPage)]="mySignal" for parent-controlled mode
   * Uncontrolled mode: Initialized from initialPage input
   */
  readonly currentPage = model<number>(1);

  /**
   * Current page size Can be bound with [(pageSize)]="mySignal" for parent-controlled mode Uncontrolled mode:
   * Initialized from initialPageSize input
   */
  readonly pageSize = model<number | null>(null);

  // ============================================================
  // OUTPUTS - Events
  // ============================================================

  /** Emitted when page number changes */
  readonly pageNumberChange = output<PaginationEvent>();

  /** Emitted when page size changes */
  readonly pageSizeChangeEvent = output<PaginationEvent>();

  // ============================================================
  // INTERNAL STATE - Uncontrolled Mode
  // ============================================================

  /** Go-to-page input value Tracks user input for validation and navigation */
  protected readonly gotoPageValue = computed(() => (this.currentPage() ?? 1).toString());

  /** Validation error message for go-to-page input Null when input is valid or empty */
  protected readonly gotoPageError = signal<string | null>(null);

  // ============================================================
  // COMPUTED SIGNALS - Derived State
  // ============================================================

  /** Total number of pages based on totalRecordCount and pageSize Always at least 1 (even for zero records) */
  protected readonly totalPages = computed(() => {
    const total = this.totalRecordCount();
    const size = this.pageSize() ?? this.initialPageSize();
    return size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;
  });

  /** Whether there is a valid previous page False on page 1 or when pageSize is 0 */
  protected readonly hasPreviousPage = computed(() => {
    const size = this.pageSize() ?? this.initialPageSize();
    return this.currentPage() > 1 && size !== 0;
  });

  /** Whether there is a valid next page False on last page or when pageSize is 0 */
  protected readonly hasNextPage = computed(() => {
    const size = this.pageSize() ?? this.initialPageSize();
    return this.currentPage() < this.totalPages() && size !== 0;
  });

  /**
   * Array of page number buttons to display Implements ellipsis strategy: show all when ≤6 pages, else [first] [...]
   * [adjacent] [...] [last]
   */
  protected readonly pageNumberButtons = computed(() =>
    this.generatePageButtons(this.currentPage(), this.totalPages())
  );
  constructor() {
    effect(() => {
      const max = this.totalPages();
      const current = this.currentPage();
      if (current > max) {
        this.currentPage.set(Math.max(1, max));
      }
    });
  }

  // ============================================================
  // LIFECYCLE
  // ============================================================

  ngOnInit(): void {
    // Initialize pageSize from initialPageSize if not using two-way binding
    if (this.pageSize() !== null) {
      return;
    }

    const requestedSize = this.initialPageSize();
    const options = this.pageSizeOptions();
    const resolvedSize =
      options.find((opt) => opt.value === requestedSize)?.value ?? options[0]?.value ?? requestedSize;

    // Use requested size if it's in the options; otherwise fall back to the first option
    this.pageSize.set(resolvedSize);
  }

  // ============================================================
  // PAGE NAVIGATION METHODS
  // ============================================================

  /** Navigate to previous page */
  protected previousPage(): void {
    if (!this.hasPreviousPage() || this.disabled()) {
      return;
    }
    const prevPage = this.currentPage();
    this.currentPage.set(prevPage - 1);
    this.emitPageNumberChangeEvent(prevPage);
  }

  /** Navigate to next page */
  protected nextPage(): void {
    if (!this.hasNextPage() || this.disabled()) {
      return;
    }
    const prevPage = this.currentPage();
    this.currentPage.set(prevPage + 1);
    this.emitPageNumberChangeEvent(prevPage);
  }

  /** Navigate to specific page number via button click */
  protected goToPage(pageNumber: number): void {
    if (!this.isValidPageNumber(pageNumber) || this.disabled()) {
      return;
    }
    const prevPage = this.currentPage();
    this.currentPage.set(pageNumber);
    this.emitPageNumberChangeEvent(prevPage);
  }

  // ============================================================
  // PAGE SIZE CHANGE HANDLER
  // ============================================================

  /** Handle page size selection change Recalculates totalPages and resets to page 1 */
  protected onPageSizeChange(newPageSize: number): void {
    const currentPageSize = this.pageSize() ?? this.initialPageSize();
    if (this.disabled() || newPageSize === currentPageSize) {
      return;
    }

    const prevPageSize = currentPageSize;
    const prevPage = this.currentPage();

    // Update page size and reset to page 1
    this.pageSize.set(newPageSize);
    this.currentPage.set(1);
    this.gotoPageError.set(null);

    // Emit page size change event
    this.emitPageSizeChangeEvent(prevPage, prevPageSize);
  }

  // ============================================================
  // GO-TO-PAGE VALIDATION & NAVIGATION
  // ============================================================

  /** Handle go-to-page input value changes Validates input and sets error message if invalid */
  protected onGotoPageChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    if (!value || value.trim() === '') {
      this.gotoPageError.set(null);
      return;
    }

    const error = this.validateGotoPageInput(value);
    this.gotoPageError.set(error);
  }

  /** Handle go-to-page submission (Enter key or button click) Validates and navigates to entered page if valid */
  protected onGotoPageSubmit(event): void {
    const value = ((event.target as HTMLInputElement).value || '').trim();

    if (!value) {
      return;
    }

    const error = this.validateGotoPageInput(value);
    this.gotoPageError.set(error);

    if (error) {
      return;
    }

    const pageNumber = parseInt(value, 10);
    if (pageNumber === this.currentPage()) {
      return; // Already on this page
    }

    const prevPage = this.currentPage();
    this.currentPage.set(pageNumber);
    this.gotoPageError.set(null);
    this.emitPageNumberChangeEvent(prevPage);
  }

  // ============================================================
  // VALIDATION HELPERS
  // ============================================================

  /** Validate go-to-page input value Returns error message if invalid, null if valid */
  private validateGotoPageInput(value: string): string | null {
    if (!/^\d+$/.test(value)) {
      return this.gotoPageErrorMessage();
    }

    const pageNumber = parseInt(value, 10);

    if (pageNumber < 1 || pageNumber > this.totalPages()) {
      return this.gotoPageErrorMessage();
    }

    return null;
  }

  /** Check if page number is valid */
  private isValidPageNumber(pageNumber: number): boolean {
    return pageNumber >= 1 && pageNumber <= this.totalPages();
  }

  // ============================================================
  // PAGE NUMBER GENERATION
  // ============================================================

  /**
   * Generate array of page number buttons with enhanced ellipsis strategy
   *
   * When totalPages <= 6: Show all pages When totalPages > 6:
   *
   * - First/second page: Show [1 2 3 4] [...] [last]
   * - Last/second-to-last page: Show [1] [...] [last-3 last-2 last-1 last]
   * - Middle pages: Show [1] [...] [current-1 current current+1] [...] [last]
   */
  private generatePageButtons(currentPage: number, totalPages: number): PageNumberButton[] {
    if (totalPages <= 6) {
      // Show all pages
      return Array.from({ length: totalPages }, (_, i) => ({
        pageNumber: i + 1,
        isCurrentPage: i + 1 === currentPage,
        isClickable: true,
        isEllipsis: false,
        displayText: (i + 1).toString(),
      }));
    }

    // Enhanced ellipsis strategy for > 6 pages with boundary detection
    const buttons: PageNumberButton[] = [];

    // Always show first page
    buttons.push({
      pageNumber: 1,
      isCurrentPage: currentPage === 1,
      isClickable: true,
      isEllipsis: false,
      displayText: '1',
    });

    // Determine visible range based on boundary detection
    let start: number;
    let end: number;

    if (currentPage === 1 || currentPage === 2) {
      // On first or second page: show [1, 2, 3, 4]
      start = 2;
      end = Math.min(4, totalPages);
    } else if (currentPage === totalPages || currentPage === totalPages - 1) {
      // On last or second-to-last page: show [totalPages-3, totalPages-2, totalPages-1, totalPages]
      start = Math.max(2, totalPages - 3);
      end = totalPages;
    } else {
      // Middle pages: show [currentPage-1, currentPage, currentPage+1]
      start = Math.max(2, currentPage - 1);
      end = Math.min(totalPages - 1, currentPage + 1);
    }

    // Add left ellipsis if visible range doesn't start at page 2
    if (start > 2) {
      buttons.push({
        pageNumber: -1,
        isCurrentPage: false,
        isClickable: false,
        isEllipsis: true,
        displayText: '...',
      });
    }

    // Add visible page range
    for (let i = start; i <= end; i++) {
      buttons.push({
        pageNumber: i,
        isCurrentPage: i === currentPage,
        isClickable: true,
        isEllipsis: false,
        displayText: i.toString(),
      });
    }

    // Add right ellipsis if visible range doesn't end at totalPages-1
    if (end < totalPages - 1) {
      buttons.push({
        pageNumber: -1,
        isCurrentPage: false,
        isClickable: false,
        isEllipsis: true,
        displayText: '...',
      });
    }

    // Always show last page (if not already added)
    if (buttons[buttons.length - 1].pageNumber !== totalPages) {
      buttons.push({
        pageNumber: totalPages,
        isCurrentPage: currentPage === totalPages,
        isClickable: true,
        isEllipsis: false,
        displayText: totalPages.toString(),
      });
    }

    return buttons;
  }

  // ============================================================
  // EVENT EMISSION
  // ============================================================

  /** Emit page number change event */
  private emitPageNumberChangeEvent(previousPage: number): void {
    const currentPageSize = this.pageSize() ?? this.initialPageSize();
    this.pageNumberChange.emit({
      newPage: this.currentPage(),
      previousPage,
      newPageSize: currentPageSize,
      previousPageSize: currentPageSize,
      totalPages: this.totalPages(),
    });
  }

  /** Emit page size change event */
  private emitPageSizeChangeEvent(previousPage: number, previousPageSize: number): void {
    this.pageSizeChangeEvent.emit({
      newPage: this.currentPage(),
      previousPage,
      newPageSize: this.pageSize() ?? this.initialPageSize(),
      previousPageSize,
      totalPages: this.totalPages(),
    });
  }
}
